import { withContentFromProps } from '@eneco/cms';
import { cmsComponents } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

const sitecoreComponents = {
  // Navigation
  BreadCrumbs: dynamic(() => import('@eneco/sitecore-components-content/Breadcrumbs/Breadcrumbs')),
  FooterMenu: dynamic(() => import('@eneco/sitecore-components-content/Footer/FooterMenu')),
  Footer: dynamic(() => import('@eneco/sitecore-components-content/Footer/Footer')),
  LanguageSelector: dynamic(() => import('@eneco/sitecore-components-content/LanguageSelector/LanguageSelector')),
  Logo: dynamic(() => import('@eneco/sitecore-components-eneco/ImageLink/ImageLink')),
  ChatEntryPoint: dynamic(() => import('@eneco/sitecore-components-content/ChatEntryPoint/ChatEntryPoint')),
  MainNavigation: dynamic(() => import('@apps/header-shared-multilabel/Header')),
  MegaMenu: dynamic(() => import('@eneco/sitecore-components-content/MegaMenu/MegaMenu')),
  MegaMenuV2: dynamic(() => import('@eneco/sitecore-components-content/MegaMenuV2/MegaMenuV2')),
  MegaMenuDivision: dynamic(() => import('@eneco/sitecore-components-content/MegaMenuDivision/MegaMenuDivision')),
  MessageBar: dynamic(() => import('@eneco/sitecore-components-content/MessageBar/MessageBar')),
  NotificationsBar: dynamic(() => import('@eneco/sitecore-components-content/NotificationsBar/NotificationsBar')),
  SubFooterMenu: dynamic(() => import('@eneco/sitecore-components-content/Footer/SubFooterMenu')),
  SocialMediaMenu: dynamic(() => import('@eneco/sitecore-components-content/Footer/SocialMediaMenu')),

  // Content components
  PromoBox: dynamic(() => import('@eneco/sitecore-components-content/PromoBox/PromoBox')),
  ContentCategories: dynamic(() => import('@eneco/sitecore-components-content/ContentCategories/ContentCategories')),
  ChatLinks: dynamic(() => import('@eneco/sitecore-components-content/ChatLinks/ChatLinks')),
  Accordion: dynamic(() => import('@eneco/sitecore-components-content/Accordion/Accordion')),
  Hero: dynamic(() => import('@eneco/sitecore-components-content/Hero/Hero')),
  HeroRebranded: dynamic(() => import('@eneco/sitecore-components-content/HeroRebranded/HeroRebranded')),
  NotificationBox: dynamic(() => import('@eneco/sitecore-components-content/NotificationBox/NotificationBox')),
  RichText: dynamic(() => import('@eneco/sitecore-components-content/RichText/RichText')),
  TopTaskCTA: dynamic(() => import('@eneco/sitecore-components-content/TopTaskCTA/TopTaskCTA')),
  TopTasks: dynamic(() => import('@eneco/sitecore-components-content/TopTasks/TopTasks')),
  ContactCustomerService: dynamic(
    () => import('@eneco/sitecore-components-content/ContactCustomerService/ContactCustomerService'),
  ),
  ContactMethods: dynamic(() => import('@eneco/sitecore-components-content/ContactMethods/ContactMethods')),
  CustomerCareSearchTest: dynamic(
    () => import('@eneco/sitecore-components-content/CustomerCareSearchTest/CustomerCareSearchTest'),
  ),
  CustomerRating: dynamic(() => import('@eneco/sitecore-components-content/CustomerRating/CustomerRating')),
  IconList: dynamic(() => import('@eneco/sitecore-components-content/IconList/IconList')),
  Image: dynamic(() => import('@eneco/sitecore-components-content/Image/Image')),
  List: dynamic(() => import('@eneco/sitecore-components-content/List/List')),
  HeroCard: dynamic(() => import('@eneco/sitecore-components-content/HeroCard/HeroCard')),
  HeroCardRebranded: dynamic(() => import('@eneco/sitecore-components-content/HeroCardRebranded/HeroCardRebranded')),
  LinkCard: dynamic(() => import('@eneco/sitecore-components-content/LinkCard/LinkCard')),
  FeaturePreview: dynamic(() => import('@eneco/sitecore-components-content/FeaturePreview/FeaturePreview')),
  Price: dynamic(() => import('@eneco/sitecore-components-content/Price/Price')),
  StoreButtons: dynamic(() => import('@eneco/sitecore-components-content/StoreButtons/StoreButtons')),
  SimpleTable: dynamic(() => import('@eneco/sitecore-components-content/SimpleTable/SimpleTable')),
  TextStreamer: dynamic(() => import('@eneco/sitecore-components-content/TextStreamer/TextStreamer')),
  UsabillaFeedback: dynamic(() => import('@eneco/sitecore-components-system/UsabillaFeedback/UsabillaFeedback')),
  AddressFinder: dynamic(() => import('@eneco/sitecore-components-content/AddressFinder/AddressFinder')),
  AddressFinderWithMovingOption: dynamic(
    () => import('@eneco/sitecore-components-content/AddressFinder/AddressFinderWithMovingOption'),
  ),
  SmeAddressFinder: dynamic(() => import('@eneco/sitecore-components-content/AddressFinder/SmeAddressFinder')),
  AffiliateCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/CalculationTool')),

  AdvancedCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/AdvancedCalculationTool')),

  StepExplainer: dynamic(
    () => import('@eneco/sitecore-components-content/StepExplainerVertical/StepExplainerVertical'),
  ),
  CreditWarningBar: dynamic(() => import('@eneco/sitecore-components-content/CreditWarningBar/CreditWarningBar')),
  ShareOnSocials: dynamic(() => import('@eneco/sitecore-components-content/ShareOnSocials/ShareOnSocials')),
  PaginatedContent: dynamic(() => import('@eneco/sitecore-components-content/PaginatedContent/PaginatedContent')),
  EnergyStatisticsByRegion: dynamic(
    () => import('@eneco/sitecore-components-content/EnergyStatistics/EnergyStatisticsByRegion'),
  ),
  EnergyStatisticsForRegion: dynamic(
    () => import('@eneco/sitecore-components-content/EnergyStatistics/EnergyStatisticsForRegion'),
  ),
  FlowNextBestActionCTA: dynamic(() => import('@apps/flowNextBestAction-nl-eneco/FlowNextBestAction')),
  FlowThankYouHero: dynamic(() => import('@apps/flowthankyouhero-nl-eneco/FlowThankYouHero')),
  FlowThankYouSteps: dynamic(() => import('@apps/flowthankyousteps-nl-eneco/FlowThankYouSteps')),
  ButtonLink: dynamic(() => import('@eneco/sitecore-components-content/ButtonLink/ButtonLink')),
  ButtonLineUp: dynamic(() => import('@eneco/sitecore-components-content/ButtonLineUp/ButtonLineUp')),

  // Apps
  DecarbonizationPotentialBreakdown: dynamic(
    () => import('@apps/decarbonizationPotentialBreakdown-nl-eneco/DecarbonizationPotentialBreakdown'),
  ),
  HeatComparisonTool: dynamic(() => import('@apps/calculator-nl-eneco/HeatComparisonCalculator')),
  CustomerReview: dynamic(() => import('@apps/customerreview-shared-multilabel/CustomerReview')),
  DynamicPriceCardElectricityV2: dynamic(
    () => import('@eneco/sitecore-components-content/DynamicPriceCard/DynamicPriceCardElectricityV2'),
  ),
  DynamicPriceCardGasV2: dynamic(
    () => import('@eneco/sitecore-components-content/DynamicPriceCard/DynamicPriceCardGasV2'),
  ),
  DynamicPricingTariffs: dynamic(
    () => import('@apps/dynamicpricingtariffs-shared-multilabel/DynamicPricingTariffs/DynamicPricingTariffs'),
  ),
  InspirationAdviceFilter: dynamic(() => import('@apps/inspirationadvicefilter-nl-eneco/InspirationAdviceFilter')),
  // The CMS-lane block itself: this lane hands it raw Sitecore props, which it reads through `@eneco/cms`.
  IDealPaymentV2: withContentFromProps(cmsComponents.IDealPaymentV2, 'IDealPaymentV2'),

  // Thank you page components
  FlowThankYouSummary: dynamic(() => import('@apps/flowthankyousummary-nl-eneco/FlowThankYouSummary')),

  // Other
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  ErrorPage: dynamic(() => import('@eneco/sitecore-components-content/ErrorPage/ErrorPage')),
  GTM: dynamic(() => import('@apps/gtm-shared-multilabel')),
  MetaData: dynamic(() => import('@eneco/sitecore-components-system/MetaData/MetaData')),
  MetaTags: dynamic(() => import('@eneco/sitecore-components-system/MetaTags/MetaTags')),
  StructuredDataSchema: dynamic(
    () => import('@eneco/sitecore-components-system/StructuredDataSchema/StructuredDataSchema'),
  ),
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat')),
  ContractConfirmation: dynamic(
    () => import('@eneco/sitecore-components-content/ContractConfirmation/ContractConfirmation'),
  ),
  Unsubscribe: dynamic(() => import('@apps/unsubscribe-shared-multilabel/Unsubscribe')),
  Divider: dynamic(() => import('@eneco/sitecore-components-eneco/Divider/Divider')),
  PersonalizationCookieController: dynamic(
    () => import('@eneco/sitecore-components-system/PersonalizationCookieController/PersonalizationCookieController'),
  ),

  // Wrapper components
  Article: dynamic(() => import('@eneco/sitecore-components-content/Article/Article')),
  Centered: dynamic(() => import('@eneco/sitecore-components-content/Centered/Centered')),
  Section: dynamic(() => import('@eneco/sitecore-components-content/Section/Section')),
  LineUp: dynamic(() => import('@eneco/sitecore-components-content/LineUp/LineUp')),
  NavigationView: dynamic(() => import('@eneco/sitecore-components-content/NavigationView/NavigationView')),
  SplitView: dynamic(() => import('@eneco/sitecore-components-content/SplitView/SplitView')),
  Carousel: dynamic(() => import('@eneco/sitecore-components-content/Carousel/Carousel')),

  // Wrappable components
  ArticleCard: dynamic(() => import('@eneco/sitecore-components-content/ArticleCard/ArticleCard')),
  ArticleContentCard: dynamic(() => import('@eneco/sitecore-components-content/ArticleContentCard/ArticleContentCard')),
  ContentCard: dynamic(() => import('@eneco/sitecore-components-content/ContentCard/ContentCard')),
  CampaignContentCard: dynamic(
    () => import('@eneco/sitecore-components-content/CampaignContentCard/CampaignContentCard'),
  ),
  WieDoetErMeeContentCard: dynamic(
    () => import('@eneco/sitecore-components-content/CampaignContentCard/CampaignContentCard'),
  ),
  ContentCardRebranded: dynamic(
    () => import('@eneco/sitecore-components-content/ContentCardRebranded/ContentCardRebranded'),
  ),
  ProductCard: dynamic(() => import('@eneco/sitecore-components-content/ProductCard/ProductCard')),
  StickyNav: dynamic(() => import('@eneco/sitecore-components-content/StickyNav/StickyNav')),
  Table: dynamic(() => import('@eneco/sitecore-components-content/Table/Table')),
  TitleTextCTA: dynamic(() => import('@eneco/sitecore-components-content/TitleTextCTA/TitleTextCTA')),
  ModelContractTable: dynamic(() => import('@eneco/sitecore-components-content/ModelContractTable/ModelContractTable')),
  PersonalizedTitleTextCTA: dynamic(
    () => import('@eneco/sitecore-components-content/PersonalizedTitleTextCTA/PersonalizedTitleTextCTA'),
  ),
  Checklist: dynamic(() => import('@eneco/sitecore-components-content/Checklist/Checklist')),
  USPItem: dynamic(() => import('@eneco/sitecore-components-content/USPItem/USPItem')),
  Video: dynamic(() => import('@eneco/sitecore-components-content/Video/Video')),
  PersonalizedVideo: dynamic(() => import('@eneco/sitecore-components-content/PersonalizedVideo/PersonalizedVideo')),

  // MeterReadings
  'meterreadings-flow': dynamic(() => import('@apps/meterreadings-nl-multilabel/MeterReadingsFlow')),

  // AddressFinder Example
  ExampleAddressFinderForm: dynamic(
    () => import('@eneco/sitecore-components-content/ExampleAddressFinderForm/ExampleAddressFinderForm'),
  ),

  // EnergyRating
  RegistrationAccountCreate: dynamic(
    () => import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow'),
  ),
  RegistrationAccountReset: dynamic(
    () => import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow'),
  ),
  RegistrationFooter: dynamic(
    () => import('@apps/accessmanagement-nl-multilabel/components/RegistrationFooter/RegistrationFooter'),
  ),
  RegistrationHeader: dynamic(
    () => import('@apps/accessmanagement-nl-multilabel/components/RegistrationHeader/RegistrationHeader'),
  ),

  // Campaign components
  TextImageRebranded: dynamic(() => import('@eneco/sitecore-components-content/TextImageRebranded/TextImageRebranded')),

  // Forms
  GenericForm: dynamic(() => import('@eneco/sitecore-components-forms/GenericForm/GenericForm')),
  GenericFormAddressField: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormAddress/GenericFormAddressField'),
  ),
  GenericFormNameField: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormNameField/GenericFormNameField'),
  ),
  GenericFormParagraph: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormParagraph/GenericFormParagraph'),
  ),
  GenericFormSalutationField: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormSalutation/GenericFormSalutation'),
  ),
  GenericFormRadioGroup: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormRadioGroup/GenericFormRadioGroup'),
  ),
  GenericFormCheckboxField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormCheckboxField/GenericFormCheckboxField'),
  ),
  GenericFormCheckboxWithValueField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormCheckboxWithValueField/GenericFormCheckboxWithValueField'),
  ),
  GenericFormCheckboxGroup: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormCheckboxGroup/GenericFormCheckboxGroup'),
  ),
  GenericFormDateField: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormDateField/GenericFormDateField'),
  ),
  GenericFormEmailInputField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormEmailInputField/GenericFormEmailInputField'),
  ),
  GenericFormNumberInputField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormNumberInputField/GenericFormNumberInputField'),
  ),
  GenericFormPhoneNumberInputField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormPhoneNumberInputField/GenericFormPhoneNumberInputField'),
  ),
  GenericFormInputField: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormInputField/GenericFormInputField'),
  ),
  GenericFormInputHiddenField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormInputHiddenField/GenericFormInputHiddenField'),
  ),
  GenericFormTextAreaInputField: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormTextAreaInputField/GenericFormTextAreaInputField'),
  ),
  GenericFormInputSelect: dynamic(
    () =>
      import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormInputSelect/GenericFormInputSelect'),
  ),
  GenericFormDivider: dynamic(
    () => import('@eneco/sitecore-components-forms/GenericForm/Components/GenericFormDivider/GenericFormDivider'),
  ),
  ContactForm: dynamic(() => import('@eneco/sitecore-components-forms/ContactForm/ContactForm')),
  HeatDamageForm: dynamic(() => import('@eneco/sitecore-components-forms/HeatDamageForm/HeatDamageForm')),
  WarmOptimaalCampaignForm: dynamic(
    () => import('@eneco/sitecore-components-forms/WarmOptimaalCampaignForm/WarmOptimaalCampaignForm'),
  ),
  RegisterToNewsletterForm: dynamic(
    () => import('@eneco/sitecore-components-forms/RegisterToNewsletterForm/RegisterToNewsletterForm'),
  ),
  RevolutionRegisterToNewsletterForm: dynamic(
    () => import('@eneco/sitecore-components-forms/RegisterToNewsletterForm/RevolutionRegisterToNewsletterForm'),
  ),
  NewsletterFormIntegratedWithRevolution: dynamic(
    () =>
      import('@eneco/sitecore-components/forms/NewsletterFormIntegrateWithRevolutionCRM/NewsletterFormIntegrateWithRevolutionCRM'),
  ),
  EmailRequestWrapper: dynamic(
    () => import('@eneco/sitecore-components-forms/EmailRequestWrapper/EmailRequestWrapper'),
  ),
  WarmthInterruptions: dynamic(
    () => import('@eneco/sitecore-components-content/WarmthInterruptions/WarmthInterruptions'),
  ),
  WarmthCheckAddressFinder: dynamic(() => import('@eneco/sitecore-components-forms/WarmCheckerForm/WarmChecker')),
  // Flows
  'energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/EnergyFlow')),
  'urgent-energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/UrgentEnergyFlow')),
  'contract-extension-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/ContractExtensionFlow')),
  'contract-advice-flow': dynamic(() => import('@apps/flows-nl-eneco-contractAdviceFlow/ContractAdviceFlow')),
  'servicegemak-flow': dynamic(() => import('@apps/flows-nl-eneco-serviceGemakFlow/ServiceGemakFlow')),
  'cv-ketel-koop-flow': dynamic(() => import('@apps/flows-nl-eneco-cvKetelKoopFlow/CvKetelKoopFlow')),
  'solar-boiler-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-solarBoilerLeadFlow/SolarBoilerLeadFlow')),
  'heat-pump-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-heatPumpLeadFlow/HeatPumpLeadFlow')),
  'home-battery-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-homeBatteryLeadFlow/HomeBatteryLeadFlow')),
  'toon-subscription-flow': dynamic(() => import('@apps/flows-nl-eneco-toonFlow/ToonSubscriptionFlow')),
  'legislation-compass-flow': dynamic(
    () => import('@apps/flows-nl-eneco-legislationCompassFlow/LegislationCompassFlow'),
  ),
  'sme-energy-flow-v2': dynamic(() => import('@apps/flows-nl-eneco-smeEnergyFlow/SmeEnergyFlow')),
  'dongle-flow': dynamic(() => import('@apps/flows-nl-eneco-dongleFlow/DongleFlow')),
  'bundle-flow': dynamic(() => import('@apps/flows-nl-eneco-bundleFlow/BundleFlow')),
  'thermostat-flow': dynamic(() => import('@apps/flows-nl-eneco-thermostatFlow/ThermostatFlow')),
  'thermostat-cancellation-flow': dynamic(
    () => import('@apps/flows-nl-eneco-thermostatCancellationFlow/ThermostatCancellationFlow'),
  ),
  'ketelcomfort-cancelation-flow': dynamic(
    () => import('@apps/flows-nl-eneco-ketelComfortCancelationFlow/KetelComfortCancelationFlow'),
  ),
  'dongle-cancelation-flow': dynamic(() => import('@apps/flows-nl-eneco-dongleCancelationFlow/DongleCancelationFlow')),
  'toon-cancelation-flow': dynamic(() => import('@apps/flows-nl-eneco-toonCancelationFlow/ToonCancelationFlow')),
  'deceased-flow': dynamic(() => import('@apps/flows-nl-eneco-deceasedCancelationFlow/DeceasedCancelationFlow')),
  'decarbonization-lead-flow': dynamic(
    () => import('@apps/flows-nl-eneco-decarbonisationLeadFlow/DecarbonisationLeadFlow'),
  ),
  'b2b-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-b2bLeadFlow/B2BLeadFlow')),
  'heat-connection-under-construction-flow': dynamic(
    () => import('@apps/flows-nl-eneco-heatConnectionUnderConstructionFlow'),
  ),
};

export default sitecoreComponents;
